/**
 * Internal AI Task Management Page
 * Admin interface for managing internal AI tasks with DataGrid
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  IconButton,
  Chip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Pencil, RefreshCcw, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BillingModeNote } from '../../components/admin/internal-ai-task/BillingModeNote';
import {
  useInternalAITasks,
  useUpdateInternalAITask,
  useToggleInternalAITaskStatus,
} from '../../services/internal-ai-task.service';
import { InternalAITask, InternalAITaskFormValues, BillingMode } from '../../types/admin';
import { FormattedNumberTextField } from '../../components/common/FormattedNumberTextField';

// ============================================
// Form Dialog Component
// ============================================

interface TaskFormDialogProps {
  open: boolean;
  onClose: () => void;
  task?: InternalAITask | null;
}

const TaskFormDialog: React.FC<TaskFormDialogProps> = ({ open, onClose, task }) => {
  const updateMutation = useUpdateInternalAITask();

  const [formValues, setFormValues] = useState<InternalAITaskFormValues>({
    taskKey: task?.taskKey || '',
    name: task?.name || '',
    description: task?.description || '',
    billingMode: task?.billingMode || 'none',
    tokenLimit: task?.tokenLimit,
    timeoutMs: task?.timeoutMs,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof InternalAITaskFormValues, string>>>({});

  React.useEffect(() => {
    if (task) {
      setFormValues({
        taskKey: task.taskKey,
        name: task.name,
        description: task.description || '',
        billingMode: task.billingMode,
        tokenLimit: task.tokenLimit,
        timeoutMs: task.timeoutMs,
      });
    } else {
      setFormValues({
        taskKey: '',
        name: '',
        description: '',
        billingMode: 'none',
        tokenLimit: undefined,
        timeoutMs: undefined,
      });
    }
    setErrors({});
  }, [task, open]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof InternalAITaskFormValues, string>> = {};

    if (!formValues.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formValues.taskKey.trim()) {
      newErrors.taskKey = 'Task key is required';
    } else if (!/^[a-z0-9_-]+$/i.test(formValues.taskKey)) {
      newErrors.taskKey = 'Task key must contain only letters, numbers, underscores, and hyphens';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!task || !validateForm()) return;

    try {
      await updateMutation.mutateAsync({
        publicId: task.publicId,
        name: formValues.name,
        description: formValues.description,
        billingMode: formValues.billingMode,
        tokenLimit: formValues.tokenLimit ?? null,
        timeoutMs: formValues.timeoutMs ?? null,
      });
      onClose();
    } catch (error) {
      console.error('Error saving internal AI task:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Internal AI Task</DialogTitle>
      <DialogContent>
        <Box className="mt-4 space-y-4">
          <TextField
            fullWidth
            label="Task Key"
            value={formValues.taskKey}
            error={!!errors.taskKey}
            helperText={errors.taskKey || 'Code-managed task key'}
            disabled
          />

          <TextField
            fullWidth
            label="Name"
            value={formValues.name}
            onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
            error={!!errors.name}
            helperText={errors.name || 'Human-readable name'}
          />

          <TextField
            fullWidth
            label="Description"
            value={formValues.description}
            onChange={(e) => setFormValues({ ...formValues, description: e.target.value })}
            multiline
            rows={3}
            helperText="Optional description of the task"
          />

          <FormControl fullWidth>
            <InputLabel>Billing Mode</InputLabel>
            <Select
              value={formValues.billingMode}
              onChange={(e) =>
                setFormValues({ ...formValues, billingMode: e.target.value as BillingMode })
              }
              label="Billing Mode"
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="metered">Metered</MenuItem>
            </Select>
            <BillingModeNote mode={formValues.billingMode} />
          </FormControl>

          <FormattedNumberTextField
            fullWidth
            label="Token Limit"
            value={formValues.tokenLimit}
            allowEmpty
            onValueChange={(value) => setFormValues({ ...formValues, tokenLimit: value })}
            helperText="Maximum tokens allowed for this task"
          />

          <FormattedNumberTextField
            fullWidth
            label="Timeout (ms)"
            value={formValues.timeoutMs}
            allowEmpty
            onValueChange={(value) => setFormValues({ ...formValues, timeoutMs: value })}
            helperText="Timeout in milliseconds"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={updateMutation.isPending}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================
// Main Component
// ============================================

export const InternalAITaskManagement: React.FC = () => {
  const navigate = useNavigate();
  const { data: tasks = [], isLoading, refetch } = useInternalAITasks();
  const toggleStatusMutation = useToggleInternalAITaskStatus();

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<InternalAITask | null>(null);

  const handleEdit = (task: InternalAITask) => {
    setSelectedTask(task);
    setFormDialogOpen(true);
  };

  const handleToggleStatus = async (task: InternalAITask) => {
    try {
      await toggleStatusMutation.mutateAsync({
        publicId: task.publicId,
        enabled: !task.enabled,
      });
    } catch (error) {
      console.error('Error toggling task status:', error);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'taskKey',
      headerName: 'Task Key',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'billingMode',
      headerName: 'Billing Mode',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'metered' ? 'primary' : 'default'}
        />
      ),
    },
    {
      field: 'modelCount',
      headerName: 'Models',
      width: 100,
      align: 'center',
      renderCell: (params) => <Chip label={params.value || 0} size="small" variant="outlined" />,
    },
    {
      field: 'enabled',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Switch
          checked={params.value}
          onChange={() => handleToggleStatus(params.row)}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box className="flex gap-1">
          <IconButton
            size="small"
            onClick={() => navigate(`/internal-ai-tasks/${params.row.publicId}`)}
            className="text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
          >
            <Eye size={18} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            className="text-gray-600 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-700"
          >
            <Pencil size={18} />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box className="p-6">
      {/* Header */}
      <Box className="mb-6 flex items-center justify-between">
        <Box>
          <Typography variant="h4" className="font-bold text-gray-900 dark:text-slate-100">
            Internal AI Tasks
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            Manage internal AI task configurations and models
          </Typography>
        </Box>
        <Box className="flex gap-2">
          <Button variant="outlined" startIcon={<RefreshCcw size={18} />} onClick={() => refetch()}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Data Grid */}
      <Box className="rounded-xl bg-white shadow-sm dark:bg-slate-800">
        <DataGrid
          rows={tasks}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          getRowId={(row) => row.publicId}
          className="border-none"
        />
      </Box>

      {/* Form Dialog */}
      <TaskFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        task={selectedTask}
      />
    </Box>
  );
};

export default InternalAITaskManagement;
